/**
 * The username field for the edit-profile form. `changeAvailableAt` comes
 * straight from the profile's `usernameChangeAvailableAt`, which the server
 * sets to `null` when the fan is free to change and to an ISO instant when
 * they are inside the 30-day window, so this component never does date math
 * beyond formatting. This component calls no hook; the route owns the check.
 */
import { Input } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  USERNAME_AVAILABLE_NOTE,
  USERNAME_FORMAT_ERROR,
  USERNAME_HINT,
  USERNAME_LABEL,
  USERNAME_TAKEN_ERROR,
  usernameWindowHint,
} from "./editProfileLogic";

export const UsernameField = ({
  value,
  onChange,
  changeAvailableAt,
  availability,
  error,
}: {
  value: string;
  onChange: (next: string) => void;
  /** ISO string when the fan is INSIDE the 30-day window, null when they are free to change. */
  changeAvailableAt: string | null;
  /** The live check's verdict for the current value, or null when nothing has been checked. */
  availability: "available" | "taken" | "invalidFormat" | null;
  /** An inline error from a failed claim, which outranks `availability`. */
  error?: string | undefined;
}): ReactNode => {
  const isInsideWindow = changeAvailableAt !== null;
  const hint = isInsideWindow
    ? usernameWindowHint(changeAvailableAt)
    : USERNAME_HINT;

  let inputError: string | undefined;
  if (error !== undefined && error !== "") {
    inputError = error;
  } else if (availability === "taken") {
    inputError = USERNAME_TAKEN_ERROR;
  } else if (availability === "invalidFormat") {
    inputError = USERNAME_FORMAT_ERROR;
  }

  return (
    <div>
      <Input
        label={USERNAME_LABEL}
        required
        value={value}
        data-testid="edit-profile-username"
        onChange={(event) => {
          onChange(event.target.value);
        }}
        autoComplete="username"
        inputMode="text"
        disabled={isInsideWindow}
        hint={hint}
        {...(inputError === undefined ? {} : { error: inputError })}
      />
      {inputError === undefined && availability === "available" ? (
        <span className="text-caption text-success">
          {USERNAME_AVAILABLE_NOTE}
        </span>
      ) : null}
    </div>
  );
};
